import { useEffect, useMemo, useState } from 'react';
import { differenceInMinutes } from 'date-fns';
import useSlingApi from '@sdk/api/useSlingApi';
import getRealDate from '@sdk/utils/getRealDate';
import { useFeatureFlag } from '@sdk/featureFlags/useFeatureFlag';
import { FeatureFlag } from '@sdk/featureFlags/types';
import { useClientFlags } from '@sdk/session/ClientFlagsContext';
import useAuth from '@sdk/authentication/useAuth';
import { BusinessCategory, BusinessSize } from '@sdk/authentication/types';

import { config } from './config';
import { doesBusinessMatchChiliPiper } from './utils';

const MINUTES_BETWEEN_REGISTRATION_AND_SHOWING_BANNER = 5;

export const useChiliPiper = (path: string) => {
  const [isChiliPiperable, setIsChiliPiperable] = useState(false);
  const [businessCategory, setBusinessCategory] = useState<BusinessCategory | null>(null);
  const [businessSize, setBusinessSize] = useState<BusinessSize | null>(null);
  const [isChiliPiperScriptLoaded, setIsChiliPiperScriptLoaded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasRequiredTimePassed, setHasRequiredTimePassed] = useState(false);

  const { currentUser } = useAuth();
  const api = useSlingApi();
  const featureReleaseDate = useFeatureFlag(FeatureFlag.CHILI_PIPER_RELEASE_DATE);
  const { updateClientFlags } = useClientFlags();

  useEffect(() => {
    const checkApi = async () => {
      try {
        const { data } = await api.auth.getOrgDetails(currentUser.org.id);
        if (doesBusinessMatchChiliPiper(data.businessCategory, data.businessSize)) {
          setIsChiliPiperable(true);
          setBusinessCategory(data.businessCategory);
          setBusinessSize(data.businessSize);
        }
      } catch {
        // we can ignore the error as by default chili piper won't be visible
      }
    };

    void checkApi();
    // We want this hook to run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentUser?.org?.createdAt || hasRequiredTimePassed) {
      return;
    }

    let timeoutId: NodeJS.Timeout;

    const checkRequiredTimePassed = () => {
      const createdAtDate = getRealDate(currentUser.org.createdAt);
      const nowDate = getRealDate(new Date());
      const minutesPassed = differenceInMinutes(nowDate, createdAtDate);

      if (minutesPassed >= MINUTES_BETWEEN_REGISTRATION_AND_SHOWING_BANNER) {
        setHasRequiredTimePassed(true);
      } else {
        // schedule next check in remaining mintues
        const nextCheck =
          (MINUTES_BETWEEN_REGISTRATION_AND_SHOWING_BANNER - minutesPassed) * 60 * 1000;
        timeoutId = setTimeout(checkRequiredTimePassed, nextCheck);
      }
    };

    checkRequiredTimePassed();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [hasRequiredTimePassed, currentUser]);

  const shouldShowChiliPiper = useMemo(() => {
    if (
      !(featureReleaseDate instanceof Date) ||
      !currentUser?.org?.createdAt ||
      !currentUser?.settings?.clientFlags
    ) {
      return false;
    }

    const { createdAt, hasToastIntegration } = currentUser.org;
    const createdAtDate = getRealDate(createdAt);

    const shouldOrgBeIncludedByCreationDate =
      createdAtDate >= getRealDate(featureReleaseDate) && hasRequiredTimePassed;

    const { hasSeenChiliPiper } = currentUser.settings.clientFlags;

    return (
      !path.startsWith('/messages') &&
      !isDismissed &&
      shouldOrgBeIncludedByCreationDate &&
      isChiliPiperable &&
      currentUser.isManager &&
      !hasToastIntegration &&
      !hasSeenChiliPiper
    );
  }, [isChiliPiperable, currentUser, featureReleaseDate, isDismissed, hasRequiredTimePassed, path]);

  useEffect(() => {
    if (!shouldShowChiliPiper || isChiliPiperScriptLoaded) {
      return;
    }
    setIsChiliPiperScriptLoaded(true);

    const script = document.createElement('script');
    script.src = 'https://js.chilipiper.com/marketing.js';
    script.type = 'text/javascript';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [shouldShowChiliPiper, isChiliPiperScriptLoaded]);

  const markAsSeen = () => {
    updateClientFlags({ hasSeenChiliPiper: true });
    setIsDismissed(true);
  };

  const bookDemo = () => {
    try {
      window.ChiliPiper.submit(config.accountName, config.routerName, {
        map: true,
        lead: {
          FirstName: currentUser.name,
          LastName: currentUser.lastname,
          Email: currentUser.email,
          Company: currentUser.org.name,
          industry: businessCategory,
          signup_business_size__c: businessSize,
        },
        onSuccess: markAsSeen,
      });
    } catch (e) {
      console.error(e);
    }
  };

  return {
    shouldShowChiliPiper,
    markAsSeen,
    bookDemo,
  };
};
